/**
 * Dependencies
 */
import Vue from 'vue';
import axios from 'axios';
import _ from 'lodash';

/**
 * Components
 */
import Manufacturer from './components/Manufacturer';
import CheckboxFilters from './components/CheckboxFilters';
import ManufacturersSort from './components/ManufacturersSort';
import ManufacturersCount from './components/ManufacturersCount';
import PaginationNav from './components/pagination-nav';
import Search from './components/Search';

/**
 * Polyfill
 */
const queryString = require('query-string');
require('intersection-observer');

/**
 * Vue instance
 */
export default (() => {
    // Check for DOM element
    if (!document.querySelector('#products')) return false;

    return new Vue({
        el: '#products',

        components: {
            manufacturer: Manufacturer,
            'checkbox-filters': CheckboxFilters,
            'manufacturers-sort': ManufacturersSort,
            'manufacturers-count': ManufacturersCount,
            'pagination-nav': PaginationNav,
            'products-search': Search,
        },

        data: {
            loading: true,
            view: {
                filters: false,
                activeModelSearchTerm: null,
            },
            api: {
                productType: null,
                manufacturersRequest: null,
                typesRequest: null,
                productTypeIDs: [],
                iecCompliance: [],
                page: [],
                sortBy: 'alphabetical',
                search: {},
            },
            data: {
                manufacturers: [],
                manufacturersMeta: {
                    pagination: {
                        totalPage: null,
                        currentPage: null,
                    },
                },
                types: null,
            },
        },

        computed: {
            manufacturersQueryParams: function () { // eslint-disable-line object-shorthand, func-names
                // Var to contain parameters
                let params = '';

                // Get search key
                const searchKey = Object.keys(this.api.search).map(key => key);

                // If searchKey has a value
                if (this.api.search[searchKey] === '') {

                    // Stringify params
                    params = queryString.stringify({
                        types: this.api.productTypeIDs,
                        page: this.api.page,
                        iec: this.api.iecCompliance[0],
                    }, { arrayFormat: 'comma' });

                } else {

                    // Stringify params, including searchKey
                    params = queryString.stringify({
                        types: this.api.productTypeIDs,
                        page: this.api.page,
                        iec: this.api.iecCompliance[0],
                        [searchKey]: this.api.search[searchKey],
                    }, { arrayFormat: 'comma' });
                }

                // If params is empty, return existing URL
                if (!params) return this.api.manufacturersRequest;

                // Determine divider based on presence of existing params in URL
                const divider = this.api.manufacturersRequest.indexOf('?') > -1 ? '&' : '?';

                // Return computed string
                return `${this.api.manufacturersRequest}${divider}${params}`;
            },
        },

        watch: {
            /**
             * Watch computed property
             * @param {string} url URL containing query parameters
             * @param {string} prevVal Previous value
             */
            manufacturersQueryParams: function (url, prevVal) { // eslint-disable-line
                // Set loading state to true
                this.loading = true;

                // Fire new AJAX request
                this.getManufacturers(url)
                    .then((manufacturers) => {
                        // Update property with new manufacturers
                        this.data.manufacturers = manufacturers;

                        // Sort
                        this.sortManufacturers(this.api.sortBy);

                        // If previous value is not null, this is not the initial load
                        if (prevVal) {
                            // Update loading state
                            this.loading = false;
                        }
                    });
            },
        },

        mounted() {
            const manufacturerIdQuery = (new URLSearchParams(window.location.search)).get('manufacturerId');
            const manufacturerTitleQuery = (new URLSearchParams(window.location.search)).get('manufacturer');
            if (manufacturerIdQuery) {
                this.$refs.search.searchValue = manufacturerTitleQuery;
                this.api.search.manufacturerId = manufacturerIdQuery;
            }
            // Set instance property with value from DOM
            this.api.productType = this.$el.attributes['data-product-type'].value;
            this.api.productTitle = this.$el.attributes['data-product-title'].value;
            // Set manufacturers request URL
            this.api.manufacturersRequest = `/api/manufacturers/${this.api.productType}.json`;
            // Set product types request URL
            this.api.typesRequest = `/api/types/${this.api.productType}.json`;
            // Init requests
            axios.all([
                this.getFilterTypes(this.api.typesRequest),
            ])
                .then(axios.spread((types) => {
                    // Update propertes
                    this.data.types = types;

                    // Update loading state
                    this.loading = false;
                }));
        },

        methods: {
            /**
             * Get Manufacturer data
             * @param {string} requestURL The API endpoint
             */
            getManufacturers(requestURL) {
                return axios.get(requestURL)
                    // Get data
                    .then((response) => {
                        // Set property
                        this.data.manufacturersMeta = response.data.meta;
                        // Return data to next then
                        return response.data.data;
                    })
                    // Filter out Manufacturers with no Products
                    .then(data => data.filter(manufacturer => _.size(manufacturer.entries) > 0));
            },

            /**
             * Get data to use in Filter
             * @param {string} requestURL The API endpoint
             */
            getFilterTypes(requestURL) {
                return axios.get(requestURL)
                    // Get data
                    .then(response => response.data.data);
            },
            handleSearch({ key, values }) {
                this.view.activeModelSearchTerm = values.model || null;
                this.updateUrlParams({ key, values });
            },
            handlePageChange(pageNumber) {
                this.updateUrlParams({ key: 'page', values: [pageNumber] });
            },
            /**
             * Update URL parameters on main request
             * Emitted by: CheckboxFilters, Pagination, Search
             * @param {string} key Property of this.api object to update
             * @param {array} values Items to add to array
             */
            updateUrlParams({ key, values }) {
                // Is values an array?
                const isArray = Array.isArray(values);

                if (isArray) {
                    // Empty target array
                    this.api[key] = [];

                    // Add values to target array
                    values.forEach(value => this.api[key].push(value));
                } else {
                    // Update key with string
                    this.api[key] = values;
                }
                if (key !== 'page') this.api.page = 1;
            },

            /**
             * Sort Manufacturers
             * @param {string} sortBy How should manufacturers be sorted?
             */
            sortManufacturers(sortBy) {
                // If no manufacturers, stop
                if (!this.data.manufacturers) return false;

                if (!sortBy) return false;

                // Set property with new value
                this.api.sortBy = sortBy;

                if (sortBy === 'productCount') {
                    // Sort by productsCount property
                    return this.data.manufacturers.sort((a, b) => b.productsCount - a.productsCount);
                }

                // Sort alphabetically
                return this.data.manufacturers.sort((a, b) => { // eslint-disable-line arrow-body-style
                    return a.title < b.title ? -1 : 1;
                });
            },

            /**
             * Toggle view filters boolean
             */
            toggleViewFilters() {
                this.view.filters = !this.view.filters;
            },
        },

    });
})();
